import { isReservedFieldName, RECORD_LEASE_FIELD } from '@colplat/protocol';
import type { AppManifest, EntityDef, FieldDef, LeasePolicy } from './types.js';

/** Fills defaults and fails fast on manifests that cannot be served safely. */
export function defineApp(manifest: AppManifest): AppManifest {
  for (const [entityName, entity] of Object.entries(manifest.entities)) {
    validateEntity(manifest, entityName, entity);
    for (const field of Object.values(entity.fields)) {
      field.lease ??= defaultLease(field);
    }
  }
  return manifest;
}

function defaultLease(field: FieldDef): LeasePolicy {
  if (field.system) return 'none';
  if (field.type === 'bool') return 'cas';   // ADR-0007
  return 'pessimistic';
}

function validateEntity(manifest: AppManifest, name: string, entity: EntityDef): void {
  const where = `${manifest.id}.${name}`;

  if (!entity.projection) {
    throw new Error(`${where}: projection table name is required (ADR-0009)`);
  }

  if (Object.hasOwn(entity.fields, RECORD_LEASE_FIELD)) {
    throw new Error(
      `${where}: '${RECORD_LEASE_FIELD}' is reserved for the whole-record lease ` +
      `key used by document-mode entities and cannot be a real field name`,
    );
  }

  /*
   * The whole `$` prefix, not a list of the sentinels in use.
   *
   * `$record` is the whole-record lease key and `$commit` is how a document
   * commit is filed in the history table; both share a namespace with real
   * field names, and a manifest that defined one would collide silently —
   * with a lease, or with a history row. Reserving the prefix makes the
   * collision impossible rather than unlikely, and the next sentinel needs no
   * change here.
   */
  for (const fieldName of Object.keys(entity.fields)) {
    if (isReservedFieldName(fieldName)) {
      throw new Error(
        `${where}.${fieldName}: field names beginning with '$' are reserved for ` +
        'pseudo-fields such as the record lease key and document commits',
      );
    }
  }

  for (const [fieldName, field] of Object.entries(entity.fields)) {
    if (field.system && field.lease && field.lease !== 'none') {
      throw new Error(`${where}.${fieldName}: system fields cannot take a lease`);
    }
    if (field.lease === 'crdt') {
      throw new Error(`${where}.${fieldName}: crdt fields are reserved, not implemented`);
    }
  }

  // A document-mode entity commits atomically, so per-field leases would allow
  // a partially valid record to exist. See ADR-0010.
  if (entity.editMode === 'document') {
    const perField = Object.entries(entity.fields)
      .filter(([, f]) => f.lease === 'pessimistic' || f.lease === 'cas')
      .map(([n]) => n);
    if (perField.length > 0 && entity.offline === 'full') {
      throw new Error(
        `${where}: document mode with offline:'full' has no safe merge for an ` +
        `atomic record. Use offline:'create' (ADR-0011).`,
      );
    }
  }

  if (entity.parent && !manifestHasField(entity, entity.parent.field)) {
    throw new Error(`${where}: parent field '${entity.parent.field}' is not declared`);
  }

  // A document-mode child with a document-mode parent is eligible for a
  // group commit (ADR-0021) — mixing modes has no defined meaning: a
  // field-mode parent already commits per field, so there is no single
  // parent "record" for the group to gate on, and a document-mode child of
  // a field-mode parent has no group to join at all.
  if (entity.parent && entity.editMode === 'document') {
    const parentEntity = manifest.entities[entity.parent.entity];
    if (parentEntity && parentEntity.editMode !== 'document') {
      throw new Error(
        `${where}: document-mode child of field-mode parent '${entity.parent.entity}' ` +
        `has no group to join (ADR-0021) — make the parent document-mode too, or ` +
        `make this entity field-mode`,
      );
    }
  }
}

function manifestHasField(entity: EntityDef, field: string): boolean {
  return Object.hasOwn(entity.fields, field);
}
